import { makeAutoObservable, runInAction } from 'mobx';
import {
  CONFIRMATION_DEFAULT_EXPIRES_IN,
  CONFIRMATION_METHOD_KIND,
  IAuthDataSource,
  PHONE_CODE_LENGTH,
  PHONE_VERIFICATION_ERROR_KEY,
  PhoneVerificationError,
  UNEXPECTED_ERROR_KEY,
} from '../../../../../infrastructure/types/features/auth/auth-types';
import {
  LoadingStatus,
  SubmittingStatus,
} from '../../../../../../shared/infrastructure/types/common/common-types';
import { ResponseStatus } from '../../../../../../shared/infrastructure/types/api/api-types';
import { RequestPhoneVerificationParams } from '../../../../../infrastructure/types/features/auth/auth-contracts';
import { RegisterManager } from './RegisterManager';
import { YandexCloudManager } from './YandexCloudManager';

class OutgoingManager {
  registrationId?: string;
  accessToken?: string;
  phoneCode?: PhoneCode;
  codeError?: string;
  verificationError?: string;
  isWasRequest = false;

  confirmationSecondsLeft = 0;
  confirmationRequestInterval?: number;
  confirmationTimerId?: ReturnType<typeof setInterval>;

  constructor(
    private registerManager: RegisterManager,
    private yandexCloudManager: YandexCloudManager,
    private datasource: IAuthDataSource,
    private setEnterPhoneLoadingStatus: (status: LoadingStatus) => void,
    private setFormStatus: (status: SubmittingStatus) => void,
  ) {
    makeAutoObservable(this, {});
  }

  get confirmationTimer(): string {
    const safe = Math.max(0, this.confirmationSecondsLeft);
    const minutes = Math.floor(safe / 60);
    const rest = safe % 60;
    return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
  }

  get isConfirmationExpired(): boolean {
    return this.confirmationSecondsLeft <= 0;
  }

  get verificationErrorKey(): string | undefined {
    if (!this.verificationError) {
      return undefined;
    }
    return (
      PHONE_VERIFICATION_ERROR_KEY[this.verificationError as PhoneVerificationError] ??
      UNEXPECTED_ERROR_KEY
    );
  }

  setIsWasRequest(value: boolean) {
    this.isWasRequest = value;
  }

  private startConfirmationTimer(seconds?: number) {
    this.stopConfirmationTimer();
    this.confirmationSecondsLeft =
      seconds ?? this.confirmationSecondsLeft ?? CONFIRMATION_DEFAULT_EXPIRES_IN;
    if (this.confirmationSecondsLeft <= 0) {
      return;
    }
    this.confirmationTimerId = setInterval(() => this.tickConfirmationTimer(), 1000);
  }

  private tickConfirmationTimer() {
    if (this.confirmationSecondsLeft > 0) {
      this.confirmationSecondsLeft -= 1;
      return;
    }
    this.stopConfirmationTimer();
  }

  stopConfirmationTimer() {
    if (this.confirmationTimerId) {
      clearInterval(this.confirmationTimerId);
      this.confirmationTimerId = undefined;
    }
  }

  setPhoneCode(phoneCode: PhoneCode) {
    this.phoneCode = phoneCode;
    this.codeError = undefined;
  }

  submitPhoneCode() {
    if (!this.yandexCloudManager.refreshValidation()) {
      this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
      return;
    }

    if (!this.registrationId || !this.phoneCode || this.phoneCode.length !== PHONE_CODE_LENGTH) {
      return;
    }

    this.setEnterPhoneLoadingStatus(LoadingStatus.LOADING);
    this.codeError = undefined;

    this.datasource
      .checkPhoneCode(this.registrationId as string, this.phoneCode as PhoneCode, this.accessToken)
      .then(response => {
        this.setEnterPhoneLoadingStatus(LoadingStatus.LOADED);

        if (response.status === ResponseStatus.SUCCESS) {
          const status = response.result?.status;
          if (status === 'verified') {
            this.setFormStatus(SubmittingStatus.REQUEST_HAS_BEEN_SENT);
            this.registerManager.markPhoneVerified();
            return;
          }
          this.codeError = 'invalid_code';
          return;
        }

        if (response.status === ResponseStatus.SERVICE_ERROR) {
          const error = response.error.errorData?.error;
          if (error === 'invalid_code') {
            this.codeError = 'invalid_code';
            return;
          }
          this.verificationError = error ?? UNEXPECTED_ERROR_KEY;
          return;
        }

        this.verificationError = UNEXPECTED_ERROR_KEY;
      });
  }

  requestVerification() {
    if (!this.yandexCloudManager.refreshValidation()) {
      this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
      return;
    }

    this.setEnterPhoneLoadingStatus(LoadingStatus.LOADING);

    this.verificationError = undefined;
    const retry = this.registrationId
      ? { registrationId: this.registrationId, accessToken: this.accessToken }
      : undefined;
    const yandexCloud = this.yandexCloudManager.data;
    const params = {
      phone: this.registerManager.phone as PhoneNumber,
      kind: CONFIRMATION_METHOD_KIND.outgoing,
      smart_captcha_token: this.registerManager.smartCaptchaToken as SmartCaptchaToken,
      template: 'registration' as RequestPhoneVerificationParams['template'],
      metadata: {
        ...this.registerManager.trackingManager.verificationMetadata,
        ...(yandexCloud ? { yandex_cloud: yandexCloud } : {}),
      },
    };

    this.datasource
      .requestPhoneVerification(params, retry)
      .then(response => {
        if (response.status === ResponseStatus.SUCCESS) {
          const result = response.result;
          if (!this.isWasRequest) {
            this.setIsWasRequest(true);
          }

          const id = result.verification ? result.verification.id : result.id;

          this.registrationId = String(id);
          this.accessToken = result.access_token ?? this.accessToken;
          this.confirmationRequestInterval = result.verification
            ? result.verification.request_interval
            : result.request_interval;

          this.startConfirmationTimer(this.confirmationRequestInterval ?? 0);
        } else {
          this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);

          if (response.status === ResponseStatus.SERVICE_ERROR) {
            this.verificationError = response.error.errorData?.error ?? UNEXPECTED_ERROR_KEY;
            return;
          }

          this.verificationError = UNEXPECTED_ERROR_KEY;
        }
      })
      .finally(() => this.setEnterPhoneLoadingStatus(LoadingStatus.LOADED));
  }

  refresh() {
    this.registrationId = undefined;
    this.accessToken = undefined;
    this.phoneCode = undefined;
    this.codeError = undefined;
    this.verificationError = undefined;
    this.isWasRequest = false;

    this.confirmationSecondsLeft = 0;
  }
}

export { OutgoingManager };
